/**
 * AdminMobileMenu — Compact drawer trigger for the Super Admin console on
 * mobile / tablet. Replaces the sprawling horizontal TabsList (which
 * overflowed onto the Welcome banner) with a single card that opens a
 * full-height Sheet listing every admin destination.
 *
 * Drives the same `activeTab` state the desktop sidebar uses, so nothing
 * regresses — every existing admin section (Verifications, Business
 * Management, Orders, Businesses (legacy), Spotlight, Founding,
 * Blinterest, Events, Articles, News Queue, Events Queue, Submissions,
 * Places, Analytics, Email, Claims) is still one tap away.
 */
import { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  Menu, ChevronRight, Crown, LayoutDashboard, BadgeCheck, Store,
  ShoppingBag, Building2, Star, Trophy, Sparkles, Image as ImageIcon,
  Calendar, FileText, Newspaper, Inbox, ClipboardList, MapPin,
  BarChart3, Mail, Handshake,
} from "lucide-react";

// One tab-value per row. Order mirrors the user's preferred grouping.
// `focus` is the same signal the desktop sidebar sends so /biz-mgmt can
// deep-link into its search input.
const SECTIONS = [
  { label: "Dashboard",            tab: "dashboard",    icon: LayoutDashboard },
  { label: "Search Businesses",    tab: "biz-mgmt",     icon: Store, focus: "search" },
  { label: "Business Management",  tab: "biz-mgmt",     icon: Store },
  { label: "Verifications",        tab: "verifications", icon: BadgeCheck },
  { label: "Claims",               tab: "claims",       icon: Handshake },
  { label: "Orders",               tab: "orders",       icon: ShoppingBag },
  { label: "Founding",             tab: "founding",     icon: Trophy },
  { label: "Spotlight",            tab: "spotlight",    icon: Star },
  { label: "Blinterest",           tab: "blinterest",   icon: ImageIcon },
  { label: "Events",               tab: "events",       icon: Calendar },
  { label: "News / Articles",      tab: "articles",     icon: FileText },
  { label: "News Queue",           tab: "news-queue",   icon: Newspaper },
  { label: "Events Queue",         tab: "events-queue", icon: Inbox },
  { label: "Submissions",          tab: "submissions",  icon: ClipboardList },
  { label: "Places",               tab: "places",       icon: MapPin },
  { label: "Analytics",            tab: "traffic",      icon: BarChart3 },
  { label: "Email",                tab: "email",        icon: Mail },
  // Kept but deprioritised — the new "Business Management" flow covers
  // the same ground with better UX. Keep the route/data intact.
  { label: "Businesses (legacy)",  tab: "businesses",   icon: Building2 },
];

export default function AdminMobileMenu({ activeTab, focus, onSelect }) {
  const [open, setOpen] = useState(false);
  const current = SECTIONS.find((s) => s.tab === activeTab && (s.focus ? focus === s.focus : !focus))
    || SECTIONS.find((s) => s.tab === activeTab)
    || SECTIONS[0];

  return (
    <div className="lg:hidden mb-4" data-testid="admin-mobile-menu">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-3 bg-tbc-cream border border-tbc-gold/30 rounded-2xl px-4 py-3 text-left hover:bg-tbc-cream/80 transition-colors"
            data-testid="admin-mobile-menu-trigger"
          >
            <Menu className="w-5 h-5 text-tbc-navy/70 flex-none" />
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Crown className="w-4 h-4 text-tbc-gold flex-none" />
              <div className="min-w-0">
                <p className="font-heading text-sm text-tbc-navy leading-none">Admin Menu</p>
                <p className="text-[11px] text-tbc-navy/60 mt-1 truncate">
                  Currently: <span className="font-semibold">{current.label}</span>
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-tbc-navy/50 flex-none" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[85vw] max-w-sm bg-tbc-navy text-white border-r-0 p-0 overflow-y-auto">
          <SheetHeader className="px-5 pt-6 pb-4 border-b border-white/10">
            <SheetTitle className="text-white font-heading flex items-center gap-2 text-lg">
              <Crown className="w-5 h-5 text-tbc-gold" /> Admin Menu
            </SheetTitle>
            <p className="text-[11px] text-white/60 tracking-wide uppercase text-left">
              Manage, grow, and support Black businesses.
            </p>
          </SheetHeader>
          <nav className="py-3">
            {SECTIONS.map((s) => {
              const isActive = activeTab === s.tab && (s.focus ? focus === s.focus : !focus);
              const Icon = s.icon;
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => {
                    onSelect(s.tab, s.focus || null);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm text-left transition-colors ${
                    isActive
                      ? "bg-tbc-gold/10 text-tbc-gold border-l-4 border-tbc-gold pl-4"
                      : "text-white/85 hover:text-white hover:bg-white/5 border-l-4 border-transparent"
                  }`}
                  data-testid={`admin-mobile-menu-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Icon className="w-4 h-4 flex-none" />
                  <span className="flex-1">{s.label}</span>
                  {isActive && <span className="text-[10px] tracking-widest">ACTIVE</span>}
                </button>
              );
            })}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
